import { Button } from "@meiden-connect/ui";
import {
  createContext,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { useShowToast } from "../notice/use-show-toast";
import {
  resolveToolbarChildPlacement,
  toolbarChildPlacementsEqual,
  toRelativeToolbarChildPlacement,
  type ToolbarChildPlacement,
  type ToolbarParentSide,
} from "./parent-child-panel-placement";
import { resolveSelectionPanelSafeArea } from "./selection-panel-placement";

/**
 * 子パネルが開く別ポータル（カスタム色 UI など）を登録し、既存の dismiss を優先する。
 * 配置前・選択時のどちらの親子パネルからも同じ境界を使う。
 */
export const ToolbarDismissBoundaryContext = createContext<Set<HTMLElement> | null>(null);

/** 子パネルの配置状況を、子の内側で開くポップオーバーへ読み取り専用で伝える。 */
export interface ToolbarChildPanelContextValue {
  /** 解決済みの子配置。値が変わったら、子内のポップオーバーはアンカー実矩形を読み直す。 */
  readonly placement: ToolbarChildPlacement | null;
  /** 子のスクロールコンテナ。追従購読の購読先と、focus の退避先に使う。 */
  readonly scrollContainer: HTMLElement | null;
}

/**
 * 選択時だけの SelectionPanelPlacementContext ではなく、配置前・選択時の双方へ同じ契約で提供する。
 */
export const ToolbarChildPanelContext = createContext<ToolbarChildPanelContextValue | null>(null);

/** 親トリガー 1 つ分。`preview` は現在値の表示、`mixed` は複数選択で値が割れている状態を表す。 */
export interface ToolbarParentItem<TKey extends string> {
  readonly key: TKey;
  readonly label: string;
  readonly preview: ReactNode;
  readonly mixed: boolean;
}

/** 親トリガーの表示状態を含むラベル。読み上げ名とネイティブツールチップで共用する。 */
function toolbarParentItemLabel(item: ToolbarParentItem<string>): string {
  return `${item.label}${item.mixed ? "（混在）" : ""}`;
}

/** 子の内側で最初に操作できる要素。最低限使える高さの実測に使う。 */
const INTERACTIVE_SELECTOR =
  "button, input, select, textarea, [role='radio'], [role='menuitem'], [tabindex]:not([tabindex='-1'])";

function toPixels(value: string): number {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * 子 surface の block 方向余白と、先頭の操作要素 1 つぶんを足した高さを実測する。
 * 上下どちらの展開側でもこの高さを確保できない場合だけ、子を開かない判断へ使う。
 */
function measureChildMinimumUsableHeight(child: HTMLElement): number {
  const natural = child.scrollHeight;
  const surface = child.firstElementChild;
  if (!(surface instanceof HTMLElement)) return natural;
  const style = window.getComputedStyle(surface);
  const blockExtras =
    toPixels(style.paddingTop) +
    toPixels(style.paddingBottom) +
    toPixels(style.borderTopWidth) +
    toPixels(style.borderBottomWidth);
  const control = surface.querySelector<HTMLElement>(INTERACTIVE_SELECTOR);
  // 操作要素を見つけられない場合は自然高を最小値とし、過小な上限で操作不能にしない。
  if (!control) return natural;
  return Math.min(Math.ceil(blockExtras + control.offsetHeight), natural);
}

/**
 * 親パネル 1 枚から子パネルを最大 1 枚だけ開く共通部品。
 *
 * 状態として持つのは開いている子のキー 1 件だけで、表示中の項目は毎 render `items` から導出する。
 * 「太字」のような子を開かない直接操作は `directActions` として呼び出し側から渡し、
 * この部品へ個別のドメイン知識を持ち込まない。
 *
 * 子は通常フローから外した overlay として親子ラッパーへ絶対配置する。これにより、子の開閉・
 * 切替・実寸変化で親（および選択時の外枠）の実寸が変わらず、親の近傍配置が動かない。
 */
export function ToolbarParentChildPanel<TKey extends string>({
  label,
  items,
  renderChild,
  parentSide,
  testIdPrefix,
  directActions,
  fallbackFocusRef,
}: {
  readonly label: string;
  readonly items: readonly ToolbarParentItem<TKey>[];
  readonly renderChild: (key: TKey) => ReactNode;
  /** 親が基準（選択図形・メインツールバー）のどちら側に出ているか。子の端揃えと展開側を決める。 */
  readonly parentSide: ToolbarParentSide;
  readonly testIdPrefix: string;
  readonly directActions?: ReactNode | undefined;
  readonly fallbackFocusRef?: RefObject<HTMLElement | null> | undefined;
}): JSX.Element {
  // 呼び出し側が対象コンテキストを React key にするため、別対象へ古い子は持ち越さない。
  const [activeChildKey, setActiveChildKey] = useState<TKey | undefined>(undefined);
  const [portalRoots] = useState(() => new Set<HTMLElement>());
  const [placement, setPlacement] = useState<ToolbarChildPlacement | null>(null);
  const [childElement, setChildElement] = useState<HTMLDivElement | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const triggerRefs = useRef(new Map<TKey, HTMLButtonElement>());
  // フォーカス中の要素が DOM から外れても blur が発火しない環境があるため、
  // 親子の外側へ移ったことを確認できたときだけ false へ戻す。
  const focusInsideRef = useRef(false);
  const childId = useId();
  const showToast = useShowToast();
  // 表示中の項目から毎 render 導出する。項目集合から外れたキーの子は、同じ render で描画しない。
  const activeItem = items.find((item) => item.key === activeChildKey);
  // 配置が確定するまでは開いた状態として扱わない。開けなかった場合は閉状態のまま維持する。
  const expandedKey = placement?.open === true ? activeItem?.key : undefined;

  useEffect(() => {
    const dismiss = (event: PointerEvent): void => {
      // カスタム色 UI の最初の外側操作は、既存の capture dismiss だけが扱う。
      if (portalRoots.size > 0 || !rootRef.current) return;
      if (!event.composedPath().includes(rootRef.current)) setActiveChildKey(undefined);
    };
    window.addEventListener("pointerdown", dismiss, true);
    return () => window.removeEventListener("pointerdown", dismiss, true);
  }, [portalRoots]);

  // 表示項目から外れた子は上の導出で既に描画されていない。保持していたキーを閉状態へ正規化し、
  // 消えた子にフォーカスがあった場合だけ、残っている先頭の親トリガー（項目が 0 件なら
  // 呼び出し側が渡した退避先）へフォーカスを移す。存在しないトリガーは参照しない。
  useEffect(() => {
    if (activeChildKey === undefined || activeItem !== undefined) return;
    setActiveChildKey(undefined);
    const root = rootRef.current;
    const active = document.activeElement;
    // 一度も親子へ入っていない場合と、まだ親子内または別の要素へフォーカスがある場合は動かさない。
    // 消えた要素のフォーカスは行き場を失って body（または null）へ落ちるため、そのときだけ退避する。
    if (!focusInsideRef.current || root?.contains(active)) return;
    if (active !== null && active !== document.body) return;
    const firstItem = items[0];
    const nextTrigger = firstItem ? triggerRefs.current.get(firstItem.key) : undefined;
    (nextTrigger ?? fallbackFocusRef?.current)?.focus();
  }, [activeChildKey, activeItem, items, fallbackFocusRef]);

  /**
   * 親の実矩形・子の自然寸法・安全領域だけから子の配置を解決する。
   * 親の座標には一切書き込まないため、この処理が親を動かすことはない。
   */
  const updateChildPlacement = useCallback((): void => {
    const root = rootRef.current;
    const parent = parentRef.current;
    if (!root || !parent || !childElement) return;
    const parentRect = parent.getBoundingClientRect();
    const rootRect = root.getBoundingClientRect();
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    const resolved = resolveToolbarChildPlacement({
      parent: {
        left: parentRect.left,
        top: parentRect.top,
        width: parentRect.width,
        height: parentRect.height,
      },
      // スクロールコンテナの内容寸法は上限を適用しても変わらないため、自然寸法として使える。
      child: { width: childElement.scrollWidth, height: childElement.scrollHeight },
      safeArea: resolveSelectionPanelSafeArea(viewport),
      side: parentSide,
      minimumUsableHeight: measureChildMinimumUsableHeight(childElement),
    });
    if (!resolved.open) {
      // 上下どちらにも最低限の高さを確保できないため、親へ重ねず開操作を不成立にする。
      setActiveChildKey(undefined);
      setPlacement(null);
      showToast("info", "表示領域が狭いため、この設定を開けません。画面を広げてからお試しください。");
      return;
    }
    // 子の包含ブロックは親子ラッパーの padding box なので、境界幅ぶんを足して原点をそろえる。
    const next = toRelativeToolbarChildPlacement(resolved, {
      left: rootRect.left + root.clientLeft,
      top: rootRect.top + root.clientTop,
    });
    setPlacement((previous) => (toolbarChildPlacementsEqual(previous, next) ? previous : next));
  }, [childElement, parentSide, showToast]);

  // DOM の描画結果を読むため、通常の effect ではなく paint 前の layout effect で配置する。
  useLayoutEffect(() => {
    if (!activeItem || !childElement) {
      setPlacement((previous) => (previous === null ? previous : null));
      return;
    }
    updateChildPlacement();
  });

  useEffect(() => {
    if (!childElement) return undefined;
    const onResize = (): void => updateChildPlacement();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [childElement, updateChildPlacement]);

  // 親の実寸変化（項目構成・preview 幅）と、子の内容量の変化を再配置の契機にする。
  // 上限適用による子の外寸変化では自然寸法が変わらないため、同値判定で有限回に収束する。
  useEffect(() => {
    const parent = parentRef.current;
    if (!childElement || !parent || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => updateChildPlacement());
    observer.observe(parent);
    observer.observe(childElement);
    return () => observer.disconnect();
  }, [childElement, updateChildPlacement]);

  const childContext = useMemo<ToolbarChildPanelContextValue>(
    () => ({ placement, scrollContainer: childElement }),
    [placement, childElement]
  );

  return (
    <ToolbarDismissBoundaryContext.Provider value={portalRoots}>
      {/* DOM は「親 → 子」の論理順で並べ、子は通常フロー外の overlay として親へ相対配置する。 */}
      <div
        ref={rootRef}
        className="canvas-toolbar-panel-family"
        data-side={parentSide}
        onFocus={() => {
          focusInsideRef.current = true;
        }}
        onBlur={(event) => {
          const next = event.relatedTarget;
          if (next instanceof Node && !rootRef.current?.contains(next)) {
            focusInsideRef.current = false;
          }
        }}
        onKeyDown={(event) => {
          if (event.key !== "Escape" || event.nativeEvent.isComposing || !activeItem) return;
          if (portalRoots.size > 0 || !rootRef.current?.contains(document.activeElement)) return;
          event.preventDefault();
          event.stopPropagation();
          setActiveChildKey(undefined);
          triggerRefs.current.get(activeItem.key)?.focus();
        }}
      >
        <div
          ref={parentRef}
          className="canvas-toolbar-parent"
          role="toolbar"
          aria-label={label}
          data-testid={`${testIdPrefix}-parent`}
        >
          {items.map((item) => (
            <Button
              key={item.key}
              ref={(element: HTMLButtonElement | null) => {
                if (element) triggerRefs.current.set(item.key, element);
                else triggerRefs.current.delete(item.key);
              }}
              type="button"
              // 子を開く準備操作なので intent、開いている間は選択中ツールと同じ emphasis で強調する。
              variant={expandedKey === item.key ? "intent emphasis" : "intent"}
              // 正方形の寸法契約は、呼び出し側の付け忘れを防ぐためここで一括適用する。
              className="canvas-icon-button canvas-toolbar-parent-trigger canvas-toolbar-square-button"
              aria-label={toolbarParentItemLabel(item)}
              title={toolbarParentItemLabel(item)}
              aria-expanded={expandedKey === item.key}
              aria-controls={expandedKey === item.key ? childId : undefined}
              data-testid={`${testIdPrefix}-trigger-${item.key}`}
              onClick={() =>
                setActiveChildKey((current) => (current === item.key ? undefined : item.key))
              }
            >
              <span aria-hidden="true" className="canvas-toolbar-parent-preview">
                {item.preview}
              </span>
            </Button>
          ))}
          {directActions}
        </div>
        {activeItem ? (
          <ToolbarChildPanelContext.Provider value={childContext}>
            <div
              id={childId}
              ref={setChildElement}
              role="group"
              tabIndex={-1}
              aria-label={activeItem.label}
              className="canvas-toolbar-child"
              data-testid={`${testIdPrefix}-child`}
              data-direction={placement?.open === true ? placement.direction : undefined}
              style={
                placement?.open === true
                  ? {
                      left: `${placement.left}px`,
                      top: `${placement.top}px`,
                      maxWidth: `${placement.maxWidth}px`,
                      maxHeight: `${placement.maxHeight}px`,
                    }
                  : // 初回実寸を測る間は、未配置の座標から確定位置へ飛ぶ 1 フレームを見せない。
                    { visibility: "hidden" }
              }
            >
              {renderChild(activeItem.key)}
            </div>
          </ToolbarChildPanelContext.Provider>
        ) : null}
      </div>
    </ToolbarDismissBoundaryContext.Provider>
  );
}
